import React, { useEffect, useRef, useState } from "react";

export const MODAL_FIELD_CLS =
  "w-full appearance-none bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line)] rounded-xl px-3 py-2.5 text-sm text-[var(--c-ink)] outline-none placeholder:text-[var(--c-ink-4)] focus:ring-1 focus:ring-[color:var(--modal-accent)] focus:border-[color:var(--modal-accent)] disabled:opacity-50 disabled:cursor-not-allowed";

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[11px] font-semibold text-[var(--c-ink-3)] tracking-wide">
      {children}
    </label>
  );
}

export function modalShellStyle(accent: string): React.CSSProperties {
  return {
    ["--modal-accent" as string]: accent,
    borderColor: `${accent}44`,
    boxShadow: `0 0 0 1px ${accent}22, 0 28px 80px -24px ${accent}66`,
  } as React.CSSProperties;
}

export function getFactionBannerColor(label: string | null | undefined): string {
  const f = (label || "").toLowerCase();
  if (f.includes("alliance")) return "#3b82f6";
  if (f.includes("horde")) return "#ef4444";
  return "#eab308";
}

export function isLightHex(hex: string): boolean {
  const h = hex.replace("#", "");
  if (h.length < 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 180;
}

const CLASS_ICON_SLUGS: Record<string, string> = {
  Warrior: "classicon_warrior",
  Paladin: "classicon_paladin",
  Hunter: "classicon_hunter",
  Rogue: "classicon_rogue",
  Priest: "classicon_priest",
  Shaman: "classicon_shaman",
  Mage: "classicon_mage",
  Warlock: "classicon_warlock",
  Druid: "classicon_druid",
  DeathKnight: "classicon_deathknight",
  Monk: "classicon_monk",
  DemonHunter: "classicon_demonhunter",
  Evoker: "classicon_evoker",
};

export function getZamClassIconUrl(classLabel: string | null | undefined): string | null {
  if (!classLabel) return null;
  const slug = CLASS_ICON_SLUGS[classLabel.replace(/\s+/g, "")];
  return slug ? `https://wow.zamimg.com/images/wow/icons/large/${slug}.jpg` : null;
}

export function optionIconUrl(
  opt: { image_url?: string; label?: string; metadata?: any } | null | undefined,
  fallbackClassLabel?: string | null
): string | null {
  if (opt?.image_url) return opt.image_url;
  if (typeof opt?.metadata?.image_url === "string") return opt.metadata.image_url;
  return getZamClassIconUrl(opt?.label) || getZamClassIconUrl(fallbackClassLabel);
}

export function CrossfadeImage({
  src,
  alt = "",
  className = "",
  imgClassName = "absolute inset-0 h-full w-full object-cover",
}: {
  src: string | null;
  alt?: string;
  className?: string;
  imgClassName?: string;
}) {
  const [current, setCurrent] = useState<string | null>(src);
  const [previous, setPrevious] = useState<string | null>(null);
  const [showCurrent, setShowCurrent] = useState(Boolean(src));
  const shownRef = useRef<string | null>(src);
  const genRef = useRef(0);

  useEffect(() => {
    if (src === shownRef.current) return;
    const from = shownRef.current;
    const gen = ++genRef.current;

    const reduced =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!src) {
      shownRef.current = null;
      setPrevious(from);
      setCurrent(null);
      setShowCurrent(false);
      return;
    }

    if (reduced) {
      shownRef.current = src;
      setPrevious(null);
      setCurrent(src);
      setShowCurrent(true);
      return;
    }

    const commit = () => {
      if (gen !== genRef.current) return;
      setPrevious(from);
      setCurrent(src);
      setShowCurrent(false);
      // One frame at opacity 0 is what gives the transition something to run
      // from. rAF never fires in a background tab, so a timer backs it up or
      // the image would sit invisible until the tab regained focus.
      const reveal = () => {
        if (gen !== genRef.current) return;
        shownRef.current = src;
        setShowCurrent(true);
      };
      requestAnimationFrame(reveal);
      window.setTimeout(reveal, 80);
    };

    const img = new Image();
    img.onload = commit;
    img.onerror = () => {
      if (gen !== genRef.current) return;
      shownRef.current = src;
      setPrevious(null);
      setCurrent(src);
      setShowCurrent(true);
    };
    img.src = src;
    if (img.complete && img.naturalWidth > 0) commit();
  }, [src]);

  const currentVisible = showCurrent;
  // The outgoing image holds only while an incoming one is still fading in.
  // Fading out to nothing (src -> null) drops it straight away, otherwise the
  // last splash would stay painted after the header goes back to idle.
  const previousVisible = Boolean(previous) && !showCurrent && Boolean(current);

  useEffect(() => {
    if (!previous || previousVisible) return;
    const t = window.setTimeout(() => setPrevious(null), 500);
    return () => window.clearTimeout(t);
  }, [previous, previousVisible]);

  return (
    <div className={`pointer-events-none overflow-hidden ${className}`}>
      {previous ? (
        <img
          src={previous}
          alt=""
          className={`crossfade-img ${imgClassName} ${previousVisible ? "opacity-100" : "opacity-0"}`}
        />
      ) : null}
      {current ? (
        <img
          src={current}
          alt={alt}
          className={`crossfade-img ${imgClassName} ${currentVisible ? "opacity-100" : "opacity-0"}`}
        />
      ) : null}
    </div>
  );
}
